/**
 * n8n Webhook 调用封装（直连 /webhook，统一超时与错误处理）
 */
import { extractErrorMessage } from './responseAdapter'

export interface N8nWebhookOptions {
  signal?: AbortSignal
  timeoutMs?: number
}

export async function postN8nWebhook<T = unknown>(
  url: string,
  body: FormData | Record<string, unknown>,
  options?: N8nWebhookOptions
): Promise<T> {
  const timeoutMs = options?.timeoutMs ?? 120000
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  const signal = options?.signal
    ? (() => {
        const combined = new AbortController()
        options.signal!.addEventListener('abort', () => combined.abort())
        controller.signal.addEventListener('abort', () => combined.abort())
        return combined.signal
      })()
    : controller.signal

  try {
    const init: RequestInit = {
      method: 'POST',
      signal,
    }
    if (body instanceof FormData) {
      init.body = body
    } else {
      init.headers = { 'Content-Type': 'application/json' }
      init.body = JSON.stringify(body)
    }

    const response = await fetch(url, init)
    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`工作流执行失败: ${response.status} ${errorText}`)
    }

    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('application/json')) {
      return (await response.json()) as T
    }
    return (await response.text()) as T
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('请求超时，请检查 n8n 服务是否可用')
    }
    throw new Error(extractErrorMessage(error))
  }
}

export function getN8nWebhookUrl(path: string): string {
  const base = import.meta.env.VITE_N8N_WEBHOOK_URL as string | undefined
  if (base && base.startsWith('http')) return base
  return path.startsWith('/') ? path : `/webhook/${path}`
}
